import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import Header from "../../header/header";
import { CustomSpinner } from "../../shared/Spinner";
import { db } from "../../../firebase";
import { KeyContactPhoneField } from "../../shared/KeyContactPhoneField";
import styles from "../keyPlayerContactsDirectory/KeyPlayerContactsDirectory.module.scss";

const PLAYER_CONTACTS_COLLECTION = "player-key-contacts";

const normalizeText = (value) => String(value || "").trim();

function hasProfileContactData(item) {
  if (!normalizeText(item.playerProfile)) return false;
  return Boolean(
    normalizeText(item.fullName) ||
      normalizeText(item.relation) ||
      normalizeText(item.cell) ||
      normalizeText(item.email) ||
      normalizeText(item.notes)
  );
}

export default function KeyPlayerContactsProfileCleanup() {
  const [authorized, setAuthorized] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [batchSize, setBatchSize] = useState(25);
  const [batchIndex, setBatchIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [phoneDraft, setPhoneDraft] = useState("");

  const loadRows = async () => {
    setLoading(true);
    try {
      const snap = await db.collection(PLAYER_CONTACTS_COLLECTION).get();
      const rows = snap.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((item) => hasProfileContactData(item))
        .sort((a, b) => {
          const aPlayer = normalizeText(a.playerName || a.playerProfile).toLowerCase();
          const bPlayer = normalizeText(b.playerName || b.playerProfile).toLowerCase();
          if (aPlayer !== bPlayer) return aPlayer.localeCompare(bPlayer);
          return normalizeText(a.fullName).toLowerCase().localeCompare(normalizeText(b.fullName).toLowerCase());
        });
      setItems(rows);
    } catch (error) {
      console.error("Error loading player key contacts cleanup rows:", error);
      toast.error("Could not load player contact rows");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authorized) {
      loadRows();
    }
  }, [authorized]);

  const filteredItems = useMemo(() => {
    const term = normalizeText(search).toLowerCase();
    if (!term) return items;
    return items.filter((item) => {
      return (
        normalizeText(item.playerName).toLowerCase().includes(term) ||
        normalizeText(item.fullName).toLowerCase().includes(term) ||
        normalizeText(item.cell).toLowerCase().includes(term) ||
        normalizeText(item.email).toLowerCase().includes(term)
      );
    });
  }, [items, search]);

  const totalBatches = Math.max(1, Math.ceil(filteredItems.length / batchSize));
  const safeBatchIndex = Math.min(batchIndex, totalBatches - 1);
  const batchStart = safeBatchIndex * batchSize;
  const pagedItems = filteredItems.slice(batchStart, batchStart + batchSize);

  const openEdit = (item) => {
    setEditingItem(item);
    setPhoneDraft(item.cell || "");
    setShowModal(true);
  };

  const closeEdit = () => {
    setShowModal(false);
    setEditingItem(null);
    setPhoneDraft("");
  };

  const savePhone = async () => {
    if (!editingItem?.id) return;
    const cleanPhone = normalizeText(phoneDraft);
    try {
      await db.collection(PLAYER_CONTACTS_COLLECTION).doc(editingItem.id).set(
        {
          cell: cleanPhone,
          updatedAt: new Date(),
        },
        { merge: true }
      );
      setItems((prev) =>
        prev.map((item) => (item.id === editingItem.id ? { ...item, cell: cleanPhone, updatedAt: new Date() } : item))
      );
      toast.success("Player profile contact phone updated");
      closeEdit();
    } catch (error) {
      console.error("Error updating player profile contact phone:", error);
      toast.error("Could not update phone");
    }
  };

  if (!authorized) {
    return (
      <div className={styles.container}>
        <Header />
        <div className={styles.content}>
          <div className={styles.title}>
            <h4>Temp Cleanup: Player Profile Key Contacts</h4>
            <p>This cleanup tool is password protected.</p>
          </div>
          <hr />
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              if (passwordInput === "cleanup") {
                setAuthorized(true);
              } else {
                toast.error("Incorrect password");
              }
            }}
          >
            <Form.Group className="mb-3" style={{ maxWidth: 320 }}>
              <Form.Label>Enter cleanup password</Form.Label>
              <Form.Control
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="Password"
              />
            </Form.Group>
            <Button type="submit" variant="primary">
              Unlock
            </Button>
          </Form>
        </div>
      </div>
    );
  }

  if (loading) return <CustomSpinner />;

  return (
    <div className={styles.container}>
      <Header />
      <div className={styles.content}>
        <div className={styles.title}>
          <h4>Temp Cleanup: Player Profile Key Contacts</h4>
          <p>Manual batch phone cleanup for contacts stored directly on player profiles.</p>
        </div>
        <hr />

        <div className={styles.controls}>
          <Form.Control
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setBatchIndex(0);
            }}
            placeholder="Search by player, contact name, phone, or email"
            className={styles.searchInput}
          />
          <div className={styles.buttons} style={{ gap: 8 }}>
            <Form.Select
              value={batchSize}
              onChange={(e) => {
                setBatchSize(Number(e.target.value));
                setBatchIndex(0);
              }}
            >
              <option value={10}>10 / batch</option>
              <option value={25}>25 / batch</option>
              <option value={50}>50 / batch</option>
            </Form.Select>
            <Button
              variant="outline-secondary"
              onClick={() => setBatchIndex((prev) => Math.max(0, prev - 1))}
              disabled={safeBatchIndex === 0}
            >
              Prev
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => setBatchIndex((prev) => Math.min(totalBatches - 1, prev + 1))}
              disabled={safeBatchIndex >= totalBatches - 1}
            >
              Next
            </Button>
          </div>
        </div>

        <div className={styles.tableHeader}>
          <span>
            Batch {safeBatchIndex + 1} / {totalBatches}
          </span>
          <span>{filteredItems.length} records</span>
        </div>

        <div className={styles.table}>
          <div className={`${styles.row} ${styles.headerRow}`}>
            <div>Player</div>
            <div>Contact</div>
            <div>Full Name</div>
            <div>Cell</div>
            <div>Email</div>
            <div>Actions</div>
          </div>
          {pagedItems.length === 0 ? (
            <div className={styles.empty}>No records in this batch.</div>
          ) : (
            pagedItems.map((item) => (
              <div key={item.id} className={styles.row}>
                <div>{item.playerName || item.playerProfile || "--"}</div>
                <div>{item.relation || "--"}</div>
                <div>{item.fullName || "--"}</div>
                <div>{item.cell || "--"}</div>
                <div>{item.email || "--"}</div>
                <div>
                  <Button variant="dark" size="sm" onClick={() => openEdit(item)}>
                    Update Phone
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <Modal show={showModal} onHide={closeEdit} centered>
        <Modal.Header closeButton>
          <Modal.Title>Update Profile Contact Phone</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ marginBottom: 12 }}>
            <strong>{editingItem?.fullName || "Unnamed Contact"}</strong>
            <div style={{ fontSize: 13, color: "#6c757d" }}>{editingItem?.playerName || editingItem?.playerProfile || "--"}</div>
          </div>
          <KeyContactPhoneField
            value={phoneDraft}
            onChange={setPhoneDraft}
            inputProps={{ id: "profile-cleanup-phone", "aria-label": "Profile contact phone" }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEdit}>
            Cancel
          </Button>
          <Button variant="primary" onClick={savePhone}>
            Save Phone
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
